import type { DeviceResponseV5 } from '@emma/api_client/types';
import { Box, Tag, Text, Tooltip } from '@lego/klik-ui';
import { createColumnHelper } from '@/components/common/table/appTable';
import { dateTimeFormat } from '@/utility/date';
import { CopyableText } from '../common/CopyableText';
import { TableStoreProvider } from '../common/table/store/Context';
import { Table } from '../common/table/Table';

type Props = {
  device: DeviceResponseV5;
  fleetId: number;
};

type ApplicationService = {
  image: string;
  service: string;
  state: string;
  createdAt: string;
  exitCode: number;
  labels: string;
  health?: string;
};

const statusColorScheme = (state: string): string => {
  if (state === 'running') return 'success';
  if (state === 'exited') return 'error';
  return 'warning';
};

const healthColorScheme = (health: string): string => {
  if (health === 'healthy') return 'success';
  if (health === 'unhealthy') return 'error';
  return 'warning';
};

const columnHelper = createColumnHelper<ApplicationService>();

const columns = columnHelper.columns([
  columnHelper.accessor('service', {
    cell: (info) => <Text fontWeight="medium">{info.getValue()}</Text>,
    header: 'Service',
    size: 200,
  }),
  columnHelper.accessor('image', {
    cell: (info) => <CopyableText copyLabel="Copy image URI" value={info.getValue()} />,
    header: 'Image',
    size: 350,
  }),
  columnHelper.accessor('state', {
    cell: (info) => (
      <Tooltip label="Is the container process running?">
        <Tag colorScheme={statusColorScheme(info.getValue())} size="sm">
          {info.getValue().toUpperCase()}
        </Tag>
      </Tooltip>
    ),
    header: 'Status',
    size: 150,
  }),
  columnHelper.display({
    id: 'health',
    cell: (info) => {
      const health = info.row.original.health;
      return health ? (
        <Tooltip label="Is the application inside the container working?">
          <Tag colorScheme={healthColorScheme(health)} size="sm">
            {health}
          </Tag>
        </Tooltip>
      ) : (
        <Text color="gray.400">—</Text>
      );
    },
    header: 'Health',
    size: 150,
  }),
  columnHelper.accessor('createdAt', {
    cell: (info) => (
      <Text fontFamily="mono" fontSize="sm">
        {new Date(info.getValue()).toLocaleString(dateTimeFormat.local)}
      </Text>
    ),
    header: 'Created At',
    size: 280,
  }),
]);

const applicationStatusDescription =
  'Live status of each container service running on the device, as reported by the device itself';

export const DeviceApplicationStatus = ({ device, fleetId }: Props) => {
  const deployment = device.deployments?.find((d) => d.fleetId === fleetId && d.isActive);

  if (!deployment?.applicationServices || !deployment.applicationStatusReportedAt) {
    return null;
  }

  const services = JSON.parse(deployment.applicationServices) as ApplicationService[];

  const description = (
    <>
      <Text color="gray.600" fontSize="sm">
        {applicationStatusDescription}
      </Text>
      <Text color="gray.600" fontSize="xs" fontWeight="bold" mt={1}>
        Reported at{' '}
        {new Date(deployment.applicationStatusReportedAt).toLocaleString(dateTimeFormat.local)}
      </Text>
    </>
  );

  return (
    <Box background="gray.50" borderRadius="md" padding={4} width="full">
      <TableStoreProvider name="ApplicationStatus">
        <Table
          columns={columns}
          count={services.length}
          data={services}
          description={description}
          hideControls={true}
          nestedStyling={true}
          title="Reported Application Status"
        />
      </TableStoreProvider>
    </Box>
  );
};
